import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { startSetOffersSettings } from '../../actions/app';
import { startSetOffers } from '../../actions/offers';
import OwlCarousel from '../../components/OwlCarousel/OwlCarousel';
import Card from '../../components/Card/Card';
import OffersWidgetItem from './../../components/Offer/OffersWidgetItem';
import { chunk } from '../../utils/utils';

const ITEMS_PER_PAGE = 4;

const owlConfig = {
  className: 'owl-theme owl-footer-dots',
  items: 1,
  loop: false,
  nav: false,
  margin: 0,
  lazyLoad: true,
};

const NewOffersWidget = ({ collapsing, localStorageName }) => {
  const dispatch = useDispatch();
  const offers = useSelector((state) => state.offers.catalog);
  const offerSettings = useSelector((state) => state.app.settings.offer);
  const [isLoading, setIsLoading] = useState(true);
  const [paginatedOffers, setPaginatedOffers] = useState(null);
  const [isWideLayout, setIsWideLayout] = useState(false);
  const contentRef = useRef(null);
  const shouldShowContent = !isLoading && paginatedOffers && paginatedOffers.length > 0;

  useEffect(() => {
    if (offers?.length > 0) {
      const nonPrivateOffers = offers.filter(({ modifies }) => !modifies?.is_private);
      const paginated = chunk(nonPrivateOffers, ITEMS_PER_PAGE);
      setPaginatedOffers(paginated);
    } else {
      setPaginatedOffers(null);
    }
  }, [offers]);

  useEffect(() => {
    dispatch(startSetOffersSettings())
      .then(() => dispatch(startSetOffers({ tab: 'new' })))
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  const renderOffersPages = (offersPage) => {
    if (!offersPage || !offerSettings) return null;

    return offersPage.map((offer) =>
      offer ? <OffersWidgetItem key={offer.id} payout={offer.short_pixels} {...offer} /> : null,
    );
  };

  const renderOffersList = () => {
    return (
      <div className="dashboard-offers-widget-list">
        {offers.map((offer) => (
          <OffersWidgetItem key={offer.id} payout={offer.short_pixels} {...offer} />
        ))}
      </div>
    );
  };

  useEffect(() => {
    const checkWidthAfterMove = () => {
      if (contentRef.current) {
        const contentWidth = contentRef.current.offsetWidth;
        setIsWideLayout(contentWidth > 550);
      }
    };

    const mutationObserver = new MutationObserver(() => {
      checkWidthAfterMove();
    });

    if (contentRef.current) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <Card
      className="dashboard-offers-widget"
      localStorageName={localStorageName}
      collapsing={collapsing}
      loading={isLoading}
    >
      <Card.Header>
        <p className="dashboard-offers-widget-title">
          <FormattedMessage id="widgets.new-offers.title" defaultMessage="New offers" />
        </p>
      </Card.Header>

      <Card.BodyNew>
        <div ref={contentRef}>
          {shouldShowContent &&
            (isWideLayout ? (
              <OwlCarousel {...owlConfig}>
                {paginatedOffers.map((page, index) => (
                  <div key={index} className="dashboard-offers-widget-page">
                    {renderOffersPages(page)}
                  </div>
                ))}
              </OwlCarousel>
            ) : (
              renderOffersList()
            ))}
        </div>
      </Card.BodyNew>
    </Card>
  );
};

export default NewOffersWidget;
